import React, { useState, useRef, useEffect } from 'react';
import Autocomplete from '@mui/joy/Autocomplete';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Chip from '@mui/joy/Chip';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import IconButton from '@mui/joy/IconButton';
import Input from '@mui/joy/Input';
import Modal from '@mui/joy/Modal';
import ModalClose from '@mui/joy/ModalClose';
import ModalDialog from '@mui/joy/ModalDialog';
import Option from '@mui/joy/Option';
import Select from '@mui/joy/Select';
import Textarea from '@mui/joy/Textarea';
import Typography from '@mui/joy/Typography';
import {
    Close,
    CloudUpload,
    AttachFile,
} from '@mui/icons-material';
import { CreateTicket, GetAllFranchises } from '../../../axios/Axios';

interface FranchiseOption {
    id: number;
    location: string;
}

interface CreateTicketModalProps {
    open: boolean;
    onClose: () => void;
    onCreated: (ticket: any) => void;
    isSuperAdmin?: boolean;
}

export default function CreateTicketModal({ open, onClose, onCreated, isSuperAdmin }: CreateTicketModalProps) {
    const [subject, setSubject] = useState('');
    const [category, setCategory] = useState('General');
    const [priority, setPriority] = useState('Medium');
    const [reason, setReason] = useState('');
    const [body, setBody] = useState('');
    const [files, setFiles] = useState<File[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [franchises, setFranchises] = useState<FranchiseOption[]>([]);
    const [selectedFranchise, setSelectedFranchise] = useState<FranchiseOption | null>(null);

    useEffect(() => {
        if (isSuperAdmin && open) {
            GetAllFranchises()
                .then(res => {
                    const list = (res.data || []).map((f: any) => ({ id: f.id, location: f.location }));
                    setFranchises(list);
                })
                .catch(() => {});
        }
    }, [isSuperAdmin, open]);

    const resetForm = () => {
        setSubject('');
        setCategory('General');
        setPriority('Medium');
        setReason('');
        setBody('');
        setFiles([]);
        setError('');
        setSelectedFranchise(null);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setFiles(prev => [...prev, ...Array.from(e.target.files!)]);
        }
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        if (e.dataTransfer.files.length > 0) {
            setFiles(prev => [...prev, ...Array.from(e.dataTransfer.files)]);
        }
    };

    const removeFile = (index: number) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
        if (isSuperAdmin && !selectedFranchise) {
            setError('Please select a franchise.');
            return;
        }
        if (!subject.trim()) {
            setError('Subject is required.');
            return;
        }
        if (!body.trim()) {
            setError('Please describe your issue.');
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            const formData = new FormData();
            formData.append('subject', subject.trim());
            formData.append('category', category);
            formData.append('priority', priority);
            formData.append('reason', reason.trim());
            formData.append('body', body.trim());
            if (isSuperAdmin && selectedFranchise) {
                formData.append('franchise_id', selectedFranchise.id.toString());
            }
            files.forEach(f => formData.append('files', f));

            const res = await CreateTicket(formData);
            resetForm();
            onCreated(res.data);
        } catch (err: any) {
            const msg = err?.response?.data?.error || err?.response?.data?.detail || 'Failed to create ticket. Please try again.';
            setError(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <ModalDialog
                sx={{
                    maxWidth: 560,
                    width: '95vw',
                    maxHeight: '90vh',
                    overflow: 'auto',
                }}
            >
                <ModalClose />
                <Typography level="h4" sx={{ fontWeight: 700, mb: 2 }}>
                    Create Support Ticket
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {isSuperAdmin && (
                        <FormControl required>
                            <FormLabel>Franchise</FormLabel>
                            <Autocomplete
                                placeholder="Select a franchise..."
                                options={franchises}
                                getOptionLabel={(opt) => opt.location}
                                value={selectedFranchise}
                                onChange={(_, val) => setSelectedFranchise(val)}
                                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                                autoFocus
                            />
                        </FormControl>
                    )}

                    <FormControl required>
                        <FormLabel>Subject</FormLabel>
                        <Input
                            placeholder="Brief summary of your issue"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            autoFocus={!isSuperAdmin}
                        />
                    </FormControl>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                        <FormControl sx={{ flex: 1, minWidth: 120 }}>
                            <FormLabel>Category</FormLabel>
                            <Select
                                value={category}
                                onChange={(_, val) => val && setCategory(val)}
                            >
                                <Option value="General">General</Option>
                                <Option value="Billing">Billing</Option>
                                <Option value="Technical">Technical</Option>
                                <Option value="Leads & Marketing">Leads & Marketing</Option>
                                <Option value="Bookings">Bookings</Option>
                                <Option value="Account">Account</Option>
                                <Option value="Other">Other</Option>
                            </Select>
                        </FormControl>

                        <FormControl sx={{ flex: 1, minWidth: 120 }}>
                            <FormLabel>Priority</FormLabel>
                            <Select
                                value={priority}
                                onChange={(_, val) => val && setPriority(val)}
                            >
                                <Option value="Low">Low</Option>
                                <Option value="Medium">Medium</Option>
                                <Option value="High">High</Option>
                                <Option value="Urgent">Urgent</Option>
                            </Select>
                        </FormControl>
                    </Box>

                    <FormControl>
                        <FormLabel>Reason / Subcategory (optional)</FormLabel>
                        <Input
                            placeholder="e.g. Invoice discrepancy, Login issue..."
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                        />
                    </FormControl>

                    <FormControl required>
                        <FormLabel>Description</FormLabel>
                        <Textarea
                            placeholder="Please describe your issue in detail. Include any relevant details that will help us assist you."
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            minRows={4}
                            maxRows={10}
                        />
                    </FormControl>

                    {/* File upload */}
                    <FormControl>
                        <FormLabel>Attachments</FormLabel>
                        <Box
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={handleDrop}
                            sx={{
                                border: '2px dashed',
                                borderColor: 'neutral.300',
                                borderRadius: 'md',
                                p: 2,
                                textAlign: 'center',
                                cursor: 'pointer',
                                transition: '0.2s',
                                '&:hover': {
                                    borderColor: 'primary.400',
                                    bgcolor: 'primary.softBg',
                                },
                            }}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileSelect}
                                multiple
                                hidden
                            />
                            <CloudUpload sx={{ fontSize: 32, color: 'text.tertiary', mb: 0.5 }} />
                            <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                                Drag & drop files here, or click to browse
                            </Typography>
                            <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                                Images, PDFs, documents up to 10 MB each
                            </Typography>
                        </Box>
                    </FormControl>

                    {files.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {files.map((f, i) => (
                                <Chip
                                    key={i}
                                    size="sm"
                                    variant="soft"
                                    startDecorator={<AttachFile sx={{ fontSize: 14 }} />}
                                    endDecorator={
                                        <IconButton size="sm" variant="plain" onClick={() => removeFile(i)}>
                                            <Close sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    }
                                >
                                    {f.name}
                                </Chip>
                            ))}
                        </Box>
                    )}

                    {error && (
                        <Typography level="body-sm" color="danger" sx={{ fontWeight: 600 }}>
                            {error}
                        </Typography>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
                        <Button variant="plain" color="neutral" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button
                            loading={isSubmitting}
                            onClick={handleSubmit}
                        >
                            Create Ticket
                        </Button>
                    </Box>
                </Box>
            </ModalDialog>
        </Modal>
    );
}
